import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import {
  Divider,
  HStack,
  Image,
  Pressable,
  Stack,
  Text,
  VStack,
} from "native-base";
import moment from "moment";
import colors from "../../constants/colors";
import location from "../../assets/icons/location.svg";
import { formatDuration } from "../../utils";

function Location(props) {
  const {
    numeral,
    title,
    isLast,
    name,
    duration,
    showDetails,
    arrivalDate,
    departureDate,
    type,
  } = props;

  return (
    <HStack alignItems="stretch" space={4}>
      <VStack alignItems="center">
        <Stack
          width={7}
          height={7}
          backgroundColor={colors.third}
          borderRadius={7}
          justifyContent="center"
          alignItems="center">
          <Text color={colors.white}>{numeral}</Text>
        </Stack>
        <VStack flex={1} justifyContent="center" alignItems="center">
          {!isLast && (
            <Stack width="2px" height="full" backgroundColor={colors.third} />
          )}
          {!isLast && showDetails && Boolean(duration) && (
            <Stack
              width={10}
              height={10}
              backgroundColor={colors.primary}
              borderRadius="50%"
              position="absolute"
              justifyContent="center"
              alignItems="center"
              zIndex={1}>
              <Text color={colors.white} textAlign="center" fontSize="10px">
                {formatDuration(duration)}
              </Text>
            </Stack>
          )}
        </VStack>
      </VStack>
      <VStack flex={1} marginBottom={isLast ? 0 : 10} space={2}>
        <Text bold>{title}</Text>
        <HStack space={2} alignItems="flex-start">
          <Image src={location} alt="location" width={5} height={5} />
          <Text>{name}</Text>
        </HStack>
        {showDetails && (
          <VStack>
            {type !== "origin" && (
              <Text>
                <Text bold>Llegada:</Text>{" "}
                {moment(arrivalDate).format("Y-MM-DD hh:mm A")}
              </Text>
            )}
            {type !== "origin" && type !== "destination" && (
              <Text>
                <Text bold>Estancia:</Text>{" "}
                {formatDuration(
                  moment(departureDate).diff(arrivalDate, "seconds"),
                  false,
                )}
              </Text>
            )}
            {type !== "destination" && (
              <Text>
                <Text bold>Salida:</Text>{" "}
                {moment(departureDate).format("Y-MM-DD hh:mm A")}
              </Text>
            )}
          </VStack>
        )}
      </VStack>
    </HStack>
  );
}

function Itinerary(props) {
  const { itinerary, detailed } = props;
  const [showDetails, setShowDetails] = useState(detailed || false);

  const getItineraryType = useCallback(
    (location) => {
      if (location.type === "origin") {
        return "Origen";
      } else if (location.type === "destination") {
        return "Destino";
      }
      const index = itinerary
        .filter(
          (_location) =>
            _location.isReturn === location.isReturn &&
            _location.type === location.type,
        )
        .findIndex((_location) => _location.placeId === location.placeId);
      if (location.type === "boardingPoint") {
        if (location.isReturn) {
          return `Punto de descenso #${index + 1}`;
        }
        return `Punto de abordaje #${index + 1}`;
      } else if (location.type === "intermediateDestination") {
        return `Destino intermedio #${index + 1}`;
      }
    },
    [itinerary],
  );

  const returnItinerary = itinerary.filter((location) => location.isReturn);

  return (
    <VStack>
      <HStack justifyContent="space-between" marginBottom={3}>
        <Text bold color={colors.primary} fontSize="lg">
          Ida
        </Text>
        <Pressable
          onPress={() => setShowDetails(!showDetails)}
          display={detailed && "none"}>
          <Text underline color={colors.primary}>
            {showDetails ? "Ocultar" : "Ver"} detalles
          </Text>
        </Pressable>
      </HStack>
      <Divider backgroundColor={colors.primary} height={1} marginBottom={5} />
      {itinerary
        .filter((location) => !location.isReturn)
        .map((location, index, { length }) => (
          <Location
            key={location.placeId}
            numeral={index + 1}
            title={getItineraryType(location)}
            name={location.name}
            duration={location.duration}
            arrivalDate={location.arrivalDate}
            departureDate={location.departureDate}
            type={location.type}
            isLast={index + 1 === length}
            showDetails={showDetails}
          />
        ))}
      {returnItinerary.length > 0 && (
        <VStack marginTop={10}>
          <Text bold color={colors.primary} fontSize="lg">
            Vuelta
          </Text>
          <Divider
            backgroundColor={colors.primary}
            height={1}
            marginBottom={5}
          />
          {returnItinerary.map((location, index, { length }) => (
            <Location
              key={location.id}
              numeral={index + 1}
              title={getItineraryType(location)}
              name={location.name}
              duration={location.duration}
              arrivalDate={location.arrivalDate}
              departureDate={location.departureDate}
              type={location.type}
              isLast={index + 1 === length}
              showDetails={showDetails}
            />
          ))}
        </VStack>
      )}
    </VStack>
  );
}

Itinerary.propTypes = {
  itinerary: PropTypes.array.isRequired,
  detailed: PropTypes.bool,
};

Itinerary.defaultProps = {
  detailed: false,
};

Location.propTypes = {
  numeral: PropTypes.number.isRequired,
  isLast: PropTypes.bool,
  title: PropTypes.string,
  name: PropTypes.string,
  duration: PropTypes.number,
  showDetails: PropTypes.bool,
  arrivalDate: PropTypes.string,
  departureDate: PropTypes.string,
  type: PropTypes.string,
};

Location.defaultProps = {
  isLast: false,
  title: "",
  name: "",
  duration: 0,
  showDetails: false,
  arrivalDate: "",
  departureDate: "",
  type: "",
};

export default React.memo(Itinerary);
